import { Chip } from '@mui/material'
import dayjs from 'dayjs'
import { CustomColumnGridConfig } from '../types'
import MeterLink from '@/components/Table/MeterLink'

const fmt = (v: any) => (v ? dayjs(v).format('DD/MM/YYYY') : '-')

const pickMeterId = (row: any) => row.id ?? row.meterId ?? row.meterNumber
const pickConsumerName = (row: any) => row.name ?? row.consumerName
const pickDate = (row: any) =>
  row.date ?? row.createdAt ?? dayjs().format('DD/MM/YYYY')
const pickActivity = (row: any) => row.activity ?? row.activityType ?? 'פעילות'
const pickActivityDescription = (row: any) =>
  row.activityDescription ?? row.description ?? 'תיאור פעילות'
const pickStatus = (row: any) => row.status ?? 'פתוח'

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'פתוח':
    case 'open':
      return '#8577E6'
    case 'בתהליך':
    case 'in-progress':
    case 'בחירה':
      return '#F79268'
    case 'בוצע':
    case 'completed':
      return '#46BE8B'
    case 'סגור':
    case 'closed':
      return '#FCEEEE'
    case 'working':
      return '#F79268'
    case 'נסגר':
    case 'shut':
      return '#46BE8B'
    case 'בטיפול':
    case 'in-treatment':
      return '#F79268'
    default:
      return '#F2FCEE'
  }
}

const getStatusTextColor = () => {
  return '#EFF2F7'
}

const parseRowDate = (valOrRow: any): number => {
  const val = valOrRow && typeof valOrRow === 'object' ? (valOrRow?.date ?? valOrRow?.createdAt) : valOrRow
  if (!val) return 0
  if (val instanceof Date) return val.getTime()
  const s = String(val).trim()
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s)
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])).getTime()
  }
  const parsed = new Date(s).getTime()
  return isNaN(parsed) ? 0 : parsed
}

const generalColumns: CustomColumnGridConfig[] = [
  {
    field: 'date',
    headerName: 'תאריך',
    minWidth: 120,
    flex: 1,
    filter: { type: 'date' },
    filterable: true,
    sortComparator: (v1: any, v2: any) => parseRowDate(v1) - parseRowDate(v2),
    renderCell: (params: any) => fmt(pickDate(params.row)),
  },
  {
    field: 'id',
    headerName: 'מספר מד',
    minWidth: 120,
    flex: 1,
    filter: { type: 'search' },
    filterable: true,
    valueGetter: (_value, row) => String(row?.id ?? row?.meterId ?? row?.meterNumber ?? ''),
    renderCell: (params: any) => {
      const id = pickMeterId(params.row)
      return <MeterLink meter={id} />
    },
  },
  {
    field: 'name',
    headerName: 'שם הצרכן',
    minWidth: 150,
    flex: 1.2,
    filter: { type: 'search' },
    filterable: true,
    valueGetter: (_value, row) => String(row?.name ?? row?.consumerName ?? ''),
    renderCell: (params: any) => pickConsumerName(params.row) ?? '-',
  },
  {
    field: 'activity',
    headerName: 'פעילות',
    minWidth: 120,
    flex: 1,
    filter: { type: 'options' },
    filterable: true,
    valueGetter: (_value, row) => String(row?.activity ?? row?.activityType ?? ''),
    renderCell: (params: any) => pickActivity(params.row) ?? '-',
  },
  {
    field: 'activityDescription',
    headerName: 'תיאור פעילות',
    minWidth: 200,
    flex: 1.5,
    filter: { type: 'search' },
    filterable: true,
    valueGetter: (_value, row) => String(row?.activityDescription ?? row?.description ?? ''),
    renderCell: (params: any) => pickActivityDescription(params.row) ?? '-',
  },
  {
    field: 'status',
    headerName: 'סטטוס',
    width: 120,
    align: 'center',
    headerAlign: 'center',
    filter: { type: 'options' },
    filterable: true,
    valueGetter: (_value, row) => String(row?.status ?? ''),
    renderCell: (params: any) => {
      const status = pickStatus(params.row)
      return (
        <Chip
          label={status}
          size="small"
          sx={{
            backgroundColor: getStatusColor(status),
            color: getStatusTextColor(),
            fontWeight: 500,
            minWidth: 80,
            border: `1px solid ${getStatusTextColor()}20`,
          }}
        />
      )
    },
  },
]

export default generalColumns
